import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import managedAxios, { isCancelError } from "@/utils/managedAxios";
import { useAuth } from "@/auth/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { API_BASE } from "@/App";
import { Loader2, Brain, TrendingUp, Target, Lock, Zap, ArrowLeft } from "lucide-react";

const PrelimsPatternPredictionPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [topic, setTopic] = useState("");
  const [result, setResult] = useState(null);

  const isPro = user?.role === "PRO";

  const handlePredict = async () => {
    if (!isPro) {
      toast({
        variant: "destructive",
        description: "🔒 This feature is PRO-only. Upgrade to unlock Pattern Prediction."
      });
      return;
    }

    if (!topic.trim()) {
      toast({
        variant: "destructive",
        description: "Please enter a topic"
      });
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const token = localStorage.getItem("token");
      const response = await managedAxios.get(
        `${API_BASE}/prelims/pattern/predict/${encodeURIComponent(topic)}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setResult(response.data);
      toast({
        description: `Pattern prediction generated for ${topic}`
      });
    } catch (err) {
      console.error("Error:", err);
      toast({
        variant: "destructive",
        description: err.response?.data?.detail || "Failed to predict pattern"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-8 space-y-6">
      {/* Back Button */}
      <Button variant="outline" onClick={() => navigate('/prelims')} className="mb-4">
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Prelims Hub
      </Button>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Pattern Prediction</h1>
          <p className="text-sm text-slate-600">
            Predict exam patterns & high-probability topics
          </p>
        </div>
        <Badge className={isPro ? "bg-amber-500" : "bg-red-500"}>
          {isPro ? "⭐ PRO" : "🔒 PRO ONLY"}
        </Badge>
      </div>

      {/* PRO-Only Lock Screen */}
      {!isPro && (
        <Card className="bg-gradient-to-br from-violet-50 to-purple-50 border-violet-300">
          <CardContent className="p-12">
            <div className="text-center space-y-4">
              <Lock className="w-16 h-16 mx-auto text-violet-600" />
              <h2 className="text-2xl font-bold text-slate-900">
                PRO Feature Locked
              </h2>
              <p className="text-slate-700 max-w-lg mx-auto">
                Analyze 20-year frequency trends, CA relevance scores, and get AI-predicted MCQs 
                for high-probability subtopics. Know exactly what UPSC is likely to ask!
              </p>
              <div className="pt-4">
                <Button className="bg-gradient-to-r from-violet-500 to-purple-600 text-white px-8">
                  Upgrade to PRO
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Interface (PRO) */}
      {isPro && (
        <>
          {/* Info Card */}
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <Brain className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-blue-900 mb-1">What you get:</p>
                  <ul className="text-sm text-slate-700 space-y-1">
                    <li>• 20-year frequency analysis & trend direction</li>
                    <li>• Current Affairs relevance score (0-100)</li>
                    <li>• High-probability subtopics for upcoming exam</li>
                    <li>• 5 AI-predicted MCQs with rationales</li>
                    <li>• Static vs CA emphasis breakdown</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Configuration Card */}
          <Card>
            <CardContent className="p-6 space-y-4">
              <div>
                <Label htmlFor="topic">Topic</Label>
                <Input
                  id="topic"
                  placeholder="e.g., Digital India, Climate Diplomacy, Financial Inclusion"
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  disabled={loading}
                />
              </div>

              <Button
                onClick={handlePredict}
                disabled={loading}
                className="w-full bg-gradient-to-r from-violet-500 to-purple-600"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Predicting Pattern...
                  </>
                ) : (
                  <>
                    <Brain className="w-4 h-4 mr-2" />
                    Predict Exam Pattern
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Results */}
          {result && (
            <div className="space-y-4">
              {/* Overview Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="border-l-4 border-l-purple-500">
                  <CardContent className="p-4">
                    <p className="text-xs text-slate-600 mb-1">20-Year Frequency</p>
                    <p className="text-3xl font-bold text-purple-600">
                      {result.frequency_20y}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">questions asked</p>
                  </CardContent>
                </Card>

                <Card className="border-l-4 border-l-green-500">
                  <CardContent className="p-4">
                    <p className="text-xs text-slate-600 mb-1">Trend Direction</p>
                    <div className="flex items-center gap-2 mt-1">
                      <TrendingUp className="w-6 h-6 text-green-600" />
                      <p className="text-lg font-bold text-green-600">
                        {result.trend}
                      </p>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-l-4 border-l-orange-500">
                  <CardContent className="p-4">
                    <p className="text-xs text-slate-600 mb-1">CA Relevance Score</p>
                    <p className="text-3xl font-bold text-orange-600">
                      {result.ca_relevance_score}/100
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Static vs CA Emphasis */}
              {result.static_vs_ca && (
                <Card>
                  <CardContent className="p-6">
                    <h3 className="font-semibold text-slate-900 mb-4">
                      Static vs Current Affairs Emphasis
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="text-center p-4 bg-blue-50 rounded-lg">
                        <p className="text-sm text-slate-600 mb-1">Static Knowledge</p>
                        <p className="text-4xl font-bold text-blue-600">
                          {result.static_vs_ca.static}%
                        </p>
                      </div>
                      <div className="text-center p-4 bg-orange-50 rounded-lg">
                        <p className="text-sm text-slate-600 mb-1">Current Affairs</p>
                        <p className="text-4xl font-bold text-orange-600">
                          {result.static_vs_ca.ca}%
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* High-Probability Subtopics */}
              {result.high_probability_subtopics && result.high_probability_subtopics.length > 0 && (
                <Card className="bg-amber-50 border-amber-200">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 mb-4">
                      <Target className="w-5 h-5 text-amber-600" />
                      <h3 className="font-semibold text-slate-900">
                        High-Probability Subtopics
                      </h3>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {result.high_probability_subtopics.map((subtopic, idx) => (
                        <div
                          key={idx}
                          className="flex items-center gap-2 p-3 bg-white rounded-lg border border-amber-200"
                        >
                          <Badge className="bg-amber-600">{idx + 1}</Badge>
                          <span className="text-sm text-slate-700">{subtopic}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Predicted MCQs */}
              {result.predicted_mcqs && result.predicted_mcqs.length > 0 && (
                <Card className="bg-green-50 border-green-200">
                  <CardContent className="p-6 space-y-4">
                    <div className="flex items-center gap-2">
                      <Zap className="w-5 h-5 text-green-600" />
                      <h3 className="font-semibold text-slate-900">
                        Practice MCQs ({result.predicted_mcqs.length})
                      </h3>
                    </div>
                    {result.predicted_mcqs.map((mcq, idx) => (
                      <div key={idx} className="bg-white rounded-lg p-4 space-y-2">
                        <div className="flex items-start gap-2">
                          <Badge variant="outline">Q{idx + 1}</Badge>
                          <div className="flex-1">
                            <p className="text-sm font-medium text-slate-900 mb-2">
                              {mcq.question}
                            </p>
                            {mcq.rationale && (
                              <div className="bg-blue-50 border border-blue-200 rounded p-2">
                                <p className="text-xs text-slate-600">
                                  <span className="font-semibold">Why UPSC might ask:</span> {mcq.rationale}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PrelimsPatternPredictionPage;
